// What the Őrszem screen makes of the watchdog's log. No React here, so
// tests/admin can hold every sentence to the move it describes: which rule a
// move went through, who let a pool out and why, what was heard and moved
// nothing, whether a source is still answering, and what the 2.6 rules made
// of the last hour.

import type { PoolWatchRow } from "@/lib/api";
import type { JudgedAnomalyCell, TimelineEntry, WatchSource } from "@/lib/tracking";
import type { Tone } from "@/components/emc/status";
import { labelOf, statusLook } from "@/components/emc/status";
import { percentText } from "@/components/emc/thresholds";

// --- the sources ------------------------------------------------------------------

/** Every source the watchdog can have, and how often it asks: the worker's
 * defaults (`DEFAULT_INTERVAL` of each source, `EMC_WATCHDOG_INTERVAL_SECONDS`,
 * `EMC_CAMPAIGN_CHECK_INTERVAL_SECONDS`). tests/admin holds the two lists
 * together. */
export const SOURCES: { source: string; intervalSeconds: number }[] = [
  { source: "postmaster", intervalSeconds: 86_400 },
  { source: "snds", intervalSeconds: 86_400 },
  { source: "ses", intervalSeconds: 3_600 },
  { source: "dnsbl", intervalSeconds: 3_600 },
  { source: "dmarc", intervalSeconds: 3_600 },
  { source: "anomaly", intervalSeconds: 3_600 },
  { source: "campaign_check", intervalSeconds: 600 },
];

/** The sources that write signals, the timeline's filter: the API's own list
 * (`SignalSource`, held to the worker's `SOURCE_NAMES`). */
export const SIGNAL_SOURCES = ["postmaster", "snds", "ses", "dnsbl", "dmarc", "anomaly"] as const;

/** Past twice its interval and the loop's hour, a source is late: the loop
 * that should have asked it has not been round. */
export function lateAfterSeconds(intervalSeconds: number): number {
  return 2 * intervalSeconds + 3_600;
}

export interface SourceRow {
  source: string;
  label: string;
  run: WatchSource | null;
  /** A `sourceRun` state: ok, failed, skipped, late, never. */
  state: string;
}

export function sourceRows(runs: WatchSource[], now: Date): SourceRow[] {
  const known = new Map(SOURCES.map((item) => [item.source, item.intervalSeconds]));
  const names = [...SOURCES.map((item) => item.source), ...runs.map((run) => run.source).filter((name) => !known.has(name))];
  return names.map((source) => {
    const run = runs.find((item) => item.source === source) ?? null;
    let state = run === null ? "never" : run.last_status;
    const interval = known.get(source);
    if (run !== null && run.last_status !== "failed" && interval !== undefined) {
      const age = (now.getTime() - new Date(run.last_run_at).getTime()) / 1000;
      if (age > lateAfterSeconds(interval)) state = "late";
    }
    return { source, label: labelOf("watchSource", source), run, state };
  });
}

// --- a pool's budget -------------------------------------------------------------

export interface Budget {
  now: number;
  full: number;
  throttled: boolean;
  /** Why nothing goes out today whatever the limit says — a cold pool has not
   * started, a stopped or retired one is held — in the Warmup page's words.
   * null while the pool sends. */
  idle: string | null;
}

export function budget(row: PoolWatchRow): Budget {
  const full = row.daily_limit_per_ip * row.ip_count;
  const now = Math.floor(full * row.throttle_factor);
  let idle: string | null = null;
  if (row.warmup_state === "cold") idle = "hideg: még nem indult, ma semmit sem küld";
  else if (row.warmup_state === "retired") idle = "leszerelve";
  else if (row.is_paused || row.warmup_state === "paused" || row.state === "paused") idle = "megállítva";
  return { now, full, throttled: now < full, idle };
}

// --- the timeline ------------------------------------------------------------------

export interface Described {
  /** The pill: the state the move left the pool in, or the signal's level. */
  pill: { domain: "poolWatch" | "signal"; state: string } | null;
  title: string;
  /** A second line: the rule, the reconciler, why nothing moved. */
  detail: string;
  /** A person's own words (a release's note), shown as a quotation. */
  quote: string | null;
  muted: boolean;
}

function said(signal: NonNullable<TimelineEntry["signal"]>): string {
  return `${labelOf("watchSource", signal.source)} · ${labelOf("watchMetric", signal.metric)} = ${signal.value}`;
}

function ruleSentence(rule: NonNullable<TimelineEntry["rule"]>): string {
  const action = rule.action === "pause" ? "megállítás" : rule.action === "throttle" ? "lassítás (a napi keret fele)" : rule.action;
  const release =
    rule.release === "manual" ? "csak ember oldhatja fel" : "magától old fel, ha a forrás megnyugszik";
  const level = statusLook("signal", rule.level).label;
  return `A döntési tábla sora: ${rule.source} · ${rule.metric} · ${level} → ${action}; ${release}.`;
}

export function describe(entry: TimelineEntry): Described {
  if (entry.kind === "signal") {
    const signal = entry.signal;
    const title = signal ? `${said(signal)}` : "jel";
    let detail =
      "Nem mozdított poolt: a pool már vissza volt fogva, vagy a jel olyan alanyról szól, amelyet egy pool sem nevez.";
    if (signal?.source === "dmarc") {
      detail =
        "A döntési tábla a DMARC-jelekre csak riaszt: ami a riportban rossz, nem feltétlenül a mi poolunk küldése.";
    } else if (signal?.level === "unknown") {
      detail = "A forrás nem volt megkérdezhető — ez nem „rendben”, hanem „nem tudjuk”.";
    }
    return {
      pill: signal ? { domain: "signal", state: signal.level } : null,
      title: signal && signal.subject !== entry.pool ? `${title} — ${signal.subject}` : title,
      detail,
      quote: null,
      muted: false,
    };
  }

  const pill = entry.to_state ? { domain: "poolWatch" as const, state: entry.to_state } : null;
  const reason = entry.reason ?? "";
  switch (entry.action) {
    case "throttle":
    case "pause":
      return {
        pill,
        title: entry.signal ? said(entry.signal) : reason,
        detail: entry.rule ? ruleSentence(entry.rule) : `Ok: ${reason}`,
        quote: null,
        muted: false,
      };
    case "sync":
      return {
        pill,
        title: reason || "a pool áll",
        detail:
          "Egyeztetés: a pool máshol állt meg — a 4. szabály napi megállítása, a feketelista-seprés vagy egy ember —, és az őrszem ehhez igazodott.",
        quote: null,
        muted: false,
      };
    case "release":
      return {
        pill,
        title: `${entry.actor ?? "?"} feloldotta`,
        detail:
          entry.from_state === "throttled"
            ? "A napi keret visszaáll a teljesre."
            : "A pool újra küldhet, ott folytatja, ahol megállt.",
        quote: reason || null,
        muted: false,
      };
    case "watching":
      return {
        pill,
        title: "újra figyeljük",
        detail:
          entry.actor === "watchdog"
            ? "Egy félbeszakadt feloldást a reconciler fejezett be."
            : "A feloldás második lépése: innentől újra a jelek döntenek.",
        quote: null,
        muted: true,
      };
    case "auto_release":
      return {
        pill,
        title: "magától feloldva",
        detail: reason ? `A forrás megnyugodott: ${reason}.` : "A forrás megnyugodott.",
        quote: null,
        muted: false,
      };
    case "retire":
      return { pill, title: "leszerelve", detail: reason, quote: null, muted: true };
    default:
      return { pill, title: reason || entry.action || "mozdulat", detail: "", quote: null, muted: false };
  }
}

export interface DayGroup {
  key: string;
  label: string;
  entries: TimelineEntry[];
}

/** The timeline in days on the reader's clock (a moment is read where the
 * reader is; the figures elsewhere are UTC days because the rule counts so). */
export function dayGroups(entries: TimelineEntry[], timeZone?: string): DayGroup[] {
  const keyOf = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" });
  const labelOfDay = new Intl.DateTimeFormat("hu-HU", { timeZone, month: "long", day: "numeric", weekday: "long" });
  const groups: DayGroup[] = [];
  for (const entry of entries) {
    const moment = new Date(entry.occurred_at);
    const key = keyOf.format(moment);
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.entries.push(entry);
    } else {
      groups.push({ key, label: labelOfDay.format(moment), entries: [entry] });
    }
  }
  return groups;
}

// --- the 2.6 grid -------------------------------------------------------------------

/** The 2.6 measures, in the order a person reads them: what went out, what
 * came back, what people did with it. */
export const METRIC_ORDER = [
  "delivered_ratio",
  "deferred_ratio",
  "delivery_latency_p95",
  "human_open_ratio",
  "click_ratio",
] as const;

const SKIP_WORD: [string, string][] = [
  ["night", "éjszakai csend"],
  ["holiday", "ünnepnap"],
  ["small sample", "kis minta"],
  ["no measure", "nincs mérés"],
  ["no profile", "nincs profil"],
  ["flat profile", "egyenletes profil"],
  ["below the floor", "a padló alatt"],
  ["not sustained", "nem tartós"],
];

export function skipWord(reason: string): string {
  const found = SKIP_WORD.find(([key]) => reason.startsWith(key));
  return found ? `${found[1]}${reason.length > found[0].length ? reason.slice(found[0].length) : ""}` : reason;
}

export function measureText(metric: string, value: number | null): string {
  if (value === null) return "—";
  if (metric === "delivery_latency_p95") return `${Math.round(value).toLocaleString("hu-HU")} mp`;
  return percentText(value * 100);
}

export interface GridCell {
  observed: string;
  expected: string;
  deviation: string;
  tone: Tone;
  /** In a word: "baj", "figyelmeztetés", "rendben", a skip reason, "még nem nézte". */
  verdict: string;
  /** Every rule's finding, one per line, for the hover. */
  title: string;
}

export interface GridPool {
  pool: string;
  hour: string;
  /** False: the loop has not judged this pool's hour; nothing is "fine" yet. */
  judged: boolean;
  /** One line for the pool: how many cells a rule spoke about, or why none. */
  summary: string;
  isps: string[];
  cells: Record<string, Record<string, GridCell | null>>;
}

const ISP_ORDER = ["gmail", "microsoft", "yahoo", "apple", "local", "other"];

function ispRank(isp: string): number {
  const index = ISP_ORDER.indexOf(isp);
  return index < 0 ? ISP_ORDER.length : index;
}

function gridCell(cell: JudgedAnomalyCell): GridCell {
  const rules = cell.rules;
  let tone: Tone = "muted";
  let verdict = "még nem nézte";
  const lines: string[] = [];
  if (rules !== null) {
    verdict = "rendben";
    const fired = rules.filter((rule) => rule.level === "bad" || rule.level === "warn");
    if (rules.some((rule) => rule.level === "bad")) tone = "danger";
    else if (fired.length > 0) tone = "warn";
    if (fired.length > 0) {
      verdict = statusLook("signal", tone === "danger" ? "bad" : "warn").label;
    } else if (rules.length > 0 && rules.every((rule) => rule.skipped)) {
      verdict = skipWord(rules[0]?.skipped ?? "");
    }
    for (const rule of rules) {
      const name = labelOf("watchMetric", rule.rule);
      const what = rule.skipped ? skipWord(rule.skipped) : statusLook("signal", rule.level).label;
      const line = rule.threshold === null ? `${name}: ${what}` : `${name}: ${what} — küszöb ${measureText(cell.metric, rule.threshold)}`;
      lines.push(line);
    }
  }
  return {
    observed: measureText(cell.metric, cell.observed),
    expected: cell.profile_days === 0 ? "nincs profil" : measureText(cell.metric, cell.expected),
    deviation:
      cell.deviation === null ? "—" : `${cell.deviation.toLocaleString("hu-HU", { maximumFractionDigits: 2 })}×`,
    tone,
    verdict,
    title: lines.length > 0 ? lines.join("\n") : rules === null ? "A szabályok még nem futottak erre az órára." : "Egyik szabály sem szólt.",
  };
}

export function anomalyGrid(cells: JudgedAnomalyCell[]): GridPool[] {
  const pools = [...new Set(cells.map((cell) => cell.pool))].sort();
  return pools.map((pool) => {
    const mine = cells.filter((cell) => cell.pool === pool);
    const isps = [...new Set(mine.map((cell) => cell.isp))].sort(
      (left, right) => ispRank(left) - ispRank(right) || left.localeCompare(right),
    );
    const grid: Record<string, Record<string, GridCell | null>> = {};
    for (const isp of isps) {
      grid[isp] = {};
      for (const metric of METRIC_ORDER) {
        const cell = mine.find((item) => item.isp === isp && item.metric === metric);
        grid[isp][metric] = cell === undefined ? null : gridCell(cell);
      }
    }
    const judged = mine.some((cell) => cell.rules !== null);
    const shown = Object.values(grid).flatMap((row) => Object.values(row)).filter((cell): cell is GridCell => cell !== null);
    const fired = shown.filter((cell) => cell.tone !== "muted").length;
    const verdicts = new Set(shown.map((cell) => cell.verdict));
    let summary = "erre az órára még nem futottak a szabályok";
    if (judged) {
      summary =
        fired > 0
          ? `${fired} cellában szólt szabály`
          : verdicts.size === 1
            ? `egyik szabály sem szólt — minden cellában: ${[...verdicts][0]}`
            : "egyik szabály sem szólt";
    }
    return { pool, hour: mine[0]?.hour ?? "", judged, summary, isps, cells: grid };
  });
}
